import { Injectable } from '@angular/core';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

export interface CsvFromArray<T = any> {
  name: string;                         // file name without extension
  rows: T[];
  columns?: { key: string; header?: string }[];
}

export interface CsvFromTable {
  name: string;                         // file name without extension
  table: HTMLTableElement;
}

@Injectable({ providedIn: 'root' })
export class CsvZipExportService {
  async exportCsvZipFromArrays<T = any>(files: CsvFromArray<T>[], zipName = 'tables.zip'): Promise<void> {
    if (!files?.length) {
      return;
    }

    const zip = new JSZip();
    for (const f of files) {
      const csv = this.buildCsvFromArray(f.rows, f.columns);
      zip.file(`${this.safeFileName(f.name)}.csv`, '\uFEFF' + csv); // BOM for Excel
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, zipName);
  }

  async exportCsvZipFromTables(files: CsvFromTable[], zipName = 'tables.zip'): Promise<void> {
    if (!files?.length) {
      return;
    }

    const zip = new JSZip();
    for (const f of files) {
      const csv = this.buildCsvFromTable(f.table);
      zip.file(`${this.safeFileName(f.name)}.csv`, '\uFEFF' + csv);
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, zipName);
  }

  // Helpers
  private buildCsvFromArray<T>(rows: T[], columns?: { key: string; header?: string }[]): string {
    if (!rows?.length) return columns?.length ? columns.map(c => c.header ?? c.key).join(',') : '';

    let headers: string[];
    let keys: string[];

    if (columns?.length) {
      headers = columns.map(c => c.header ?? c.key);
      keys = columns.map(c => c.key);
    } else {
      const first = rows[0] as any;
      keys = Object.keys(first);
      headers = keys;
    }

    const dataRows = rows.map((r: any) =>
      keys
        .map(k => this.escapeCsv(this.getByPath(r, k)))
        .join(',')
    );

    return [headers.map(h => this.escapeCsv(h)).join(','), ...dataRows].join('\r\n');
  }

  private buildCsvFromTable(table: HTMLTableElement): string {
    const rows = Array.from(table.querySelectorAll('tr'));
    const csvRows = rows.map(tr => {
      const cells = Array.from(tr.querySelectorAll('th,td'));
      return cells.map(c => this.escapeCsv((c.textContent ?? '').trim())).join(',');
    });
    return csvRows.join('\r\n');
  }

  private escapeCsv(val: any): string {
    const s = val == null ? '' : String(val);
    const needsQuoting = /[",\r\n]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsQuoting ? `"${escaped}"` : escaped;
  }

  private getByPath(obj: any, path: string): any {
    if (!path.includes('.')) return obj?.[path];
    return path.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), obj);
  }

  private safeFileName(name: string): string {
    return (name || 'file').replace(/[\\/:*?"<>|]/g, '_');
  }
}
